import { useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import {
  useAddEquipmentInspection,
  useAddEquipmentLogPhoto,
  useCancelEquipmentReservation,
  useDeleteEquipmentPhoto,
  useEquipmentByCode,
  useEquipmentInspections,
  useEquipmentItem,
  useEquipmentLoans,
  useEquipmentReservations,
  useHandOutEquipment,
  useProjects,
  useReserveEquipment,
  useReturnEquipment,
  useSetEquipmentStatus,
  useUploadEquipmentPhoto
} from '../api/queries'
import type {
  Equipment,
  EquipmentInspection,
  EquipmentLogPhoto,
  EquipmentStatus
} from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { EquipmentPhoto, EquipmentStatusBadge, formatDay } from '../components/EquipmentBits'
import { EquipmentQr } from '../components/EquipmentLabel'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { useAuthedBlob } from '../lib/photos'
import { projectOptionLabel } from '../lib/projectLabel'

function today() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

/**
 * Værktøjets egen side - den, QR-mærkatet fører til (beslutning 096).
 *
 * <para>
 * Spørgsmålet i marken er altid det samme: <b>hvem har den, og hvornår er den
 * fri?</b> Derfor står foto, indehaver og forventet retur øverst, og knapperne
 * "Tag den", "Aflevér" og "Reservér" er lige ved hånden. Resten - eftersyn,
 * historik, mærkat - står nedenunder.
 * </para>
 */
export function EquipmentDetailPage() {
  const { id } = useParams()
  const equipmentId = Number(id)
  const { user, isAdmin, isProjectManager } = useAuth()

  const { data: item, isLoading, error } = useEquipmentItem(equipmentId)
  const { data: reservations } = useEquipmentReservations(equipmentId)
  const { data: loans } = useEquipmentLoans(equipmentId)
  const { data: inspections } = useEquipmentInspections(equipmentId)

  const handOut = useHandOutEquipment()
  const returnItem = useReturnEquipment()
  const setStatus = useSetEquipmentStatus()
  const cancelReservation = useCancelEquipmentReservation()
  const uploadPhoto = useUploadEquipmentPhoto()
  const deletePhoto = useDeleteEquipmentPhoto()

  const [actionError, setActionError] = useState<string | null>(null)
  const [reserving, setReserving] = useState(false)
  const [writingService, setWritingService] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!item) return <ErrorBox error={new Error('Værktøjet findes ikke.')} />

  const loan = item.currentLoan
  const isMine = loan?.user.id === user?.id
  const canReturn = loan != null && (isProjectManager || isMine)

  async function take() {
    setActionError(null)
    try {
      await handOut.mutateAsync({ id: equipmentId })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Grejet kunne ikke tages.')
    }
  }

  async function giveBack() {
    setActionError(null)
    try {
      await returnItem.mutateAsync({ id: equipmentId, note: null })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Grejet kunne ikke registreres som afleveret.')
    }
  }

  async function changeStatus(next: EquipmentStatus) {
    setActionError(null)

    // Naar noget saettes ud af drift, skal der staa hvorfor - ellers ved den
    // naeste ikke, om det er en loes skrue eller en knaekket vange.
    const note =
      next === 'ok' ? null : prompt(next === 'service' ? 'Hvad skal der ses på?' : 'Hvad er der galt?')

    if (next !== 'ok' && note === null) return

    try {
      await setStatus.mutateAsync({ id: equipmentId, status: next, note })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Tilstanden kunne ikke skiftes.')
    }
  }

  async function cancel(reservationId: number) {
    setActionError(null)
    if (!confirm('Aflys reservationen?')) return

    try {
      await cancelReservation.mutateAsync({ id: equipmentId, reservationId })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Reservationen kunne ikke aflyses.')
    }
  }

  async function pickPhoto(file: File | undefined) {
    if (!file) return
    setActionError(null)

    try {
      // Skaleres i browseren som sagernes fotos - et mobilbillede fylder 4-8 MB råt.
      const prepared = await prepareDocumentFile(file)
      await uploadPhoto.mutateAsync({ id: equipmentId, file: prepared.blob, fileName: prepared.fileName })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Billedet kunne ikke gemmes.')
    }
  }

  async function removePhoto() {
    setActionError(null)
    if (!confirm('Fjern fotoet af værktøjet?')) return

    try {
      await deletePhoto.mutateAsync(equipmentId)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Fotoet kunne ikke fjernes.')
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card>
        <div className="row" style={{ gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <EquipmentPhoto item={item} size={120} radius={12} />

          <div style={{ minWidth: 220, flex: 1 }}>
            <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <h1 className="page-title" style={{ margin: 0 }}>
                {item.name}
              </h1>
              <EquipmentStatusBadge status={item.status} label={item.statusLabel} showOk />
              {!item.isActive && <span className="tiny muted">kasseret</span>}
            </div>

            <div className="small muted" style={{ marginTop: 4 }}>
              {item.categoryName ?? 'Uden kategori'}
              {item.equipmentType && ` · ${item.equipmentType}`}
              {item.manufacturer && ` · ${item.manufacturer}`}
              {item.serialNumber && ` · nr. ${item.serialNumber}`}
              {item.identifier && ` · mærkning ${item.identifier}`}
            </div>

            <div style={{ marginTop: 10 }}>
              {loan ? (
                <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                  <Avatar user={loan.user} size={30} />
                  <div>
                    <div className="small" style={{ fontWeight: 600 }}>
                      {isMine ? 'Du har den' : `${loan.user.displayName} har den`}
                    </div>
                    <div className="tiny muted">
                      Taget {formatDay(loan.handedOutOn)}
                      {loan.expectedReturnOn && ` · retur ${formatDay(loan.expectedReturnOn)}`}
                      {loan.projectName && ` · ${loan.projectName}`}
                      {loan.isOverdue && ' · over tiden'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="small">
                  <strong>Den står klar.</strong>{' '}
                  <span className="muted">{item.storedAt ? `Ligger i ${item.storedAt}` : 'Ingen fast plads'}</span>
                </div>
              )}
            </div>

            <div className="btn-group" style={{ marginTop: 12 }}>
              {!loan && item.isUsable && (
                <button className="btn btn--primary" disabled={handOut.isPending} onClick={() => void take()}>
                  {handOut.isPending ? 'Et øjeblik ...' : 'Tag den'}
                </button>
              )}

              {canReturn && (
                <button className="btn" disabled={returnItem.isPending} onClick={() => void giveBack()}>
                  Aflevér
                </button>
              )}

              {item.isUsable && (
                <button className="btn" onClick={() => setReserving(true)}>
                  Reservér
                </button>
              )}

              <Link className="btn" to={`/equipment/${item.id}/label`}>
                QR-mærkat
              </Link>
            </div>

            {!item.isUsable && item.isActive && (
              <p className="small" style={{ color: '#991B1B', marginBottom: 0 }}>
                Værktøjet står som {item.statusLabel.toLowerCase()} og kan hverken tages med eller
                reserveres, før tilstanden er sat til i orden.
              </p>
            )}
          </div>

          <div style={{ textAlign: 'center' }}>
            <EquipmentQr code={item.code} size={104} />
            <div className="tiny muted" style={{ marginTop: 4, letterSpacing: 1 }}>
              {item.code}
            </div>
          </div>
        </div>

        {actionError && <ErrorBox error={actionError} />}

        {(isProjectManager || isMine) && (
          <div className="row" style={{ gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
            <span className="tiny muted" style={{ alignSelf: 'center' }}>
              Tilstand:
            </span>
            {(['ok', 'service', 'defect'] as EquipmentStatus[]).map((s) => (
              <button
                key={s}
                className={`btn btn--sm${item.status === s ? ' btn--primary' : ''}`}
                disabled={setStatus.isPending || item.status === s}
                onClick={() => void changeStatus(s)}
              >
                {s === 'ok' ? 'I orden' : s === 'service' ? 'Til service' : 'Defekt'}
              </button>
            ))}
          </div>
        )}

        {isAdmin && (
          <div className="row" style={{ gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => void pickPhoto(e.target.files?.[0])}
            />
            <button
              className="btn btn--sm"
              disabled={uploadPhoto.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {uploadPhoto.isPending ? 'Gemmer ...' : item.photoUrl ? 'Skift foto' : 'Tag eller vælg foto'}
            </button>
            {item.photoUrl && (
              <button className="btn btn--sm" disabled={deletePhoto.isPending} onClick={() => void removePhoto()}>
                Fjern foto
              </button>
            )}
          </div>
        )}
      </Card>

      <Card title="Reservationer">
        {reservations && reservations.length === 0 && (
          <EmptyState>
            Ingen har skrevet sig på. Tryk <strong>Reservér</strong>, hvis du skal bruge den en bestemt
            dag - så kan de andre se det.
          </EmptyState>
        )}

        {reservations && reservations.length > 0 && (
          <div className="stack" style={{ gap: 8 }}>
            {reservations.map((r) => (
              <div key={r.id} className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Avatar user={r.user} size={26} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div className="small" style={{ fontWeight: 600 }}>
                    {r.isMine ? 'Dig' : r.user.displayName}
                    {r.isActiveNow && <span className="tiny muted"> · gælder nu</span>}
                  </div>
                  <div className="tiny muted">
                    {formatDay(r.fromDate)} - {formatDay(r.toDate)} · {r.days}{' '}
                    {r.days === 1 ? 'dag' : 'dage'}
                    {r.projectName && ` · ${r.projectName}`}
                    {r.note && ` · ${r.note}`}
                  </div>
                </div>

                {(r.isMine || isProjectManager) && (
                  <button className="btn btn--sm" onClick={() => void cancel(r.id)}>
                    Aflys
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Service og eftersyn">
        <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <div className="small">
            {item.inspectionIntervalMonths
              ? `Efterses hver ${item.inspectionIntervalMonths}. måned · næste ${formatDay(item.nextInspectionOn)}`
              : 'Dette værktøj har ikke et fast eftersyn.'}
          </div>

          <button className="btn btn--sm" onClick={() => setWritingService(true)}>
            Skriv service
          </button>
        </div>

        {inspections && inspections.length > 0 ? (
          <div className="stack" style={{ gap: 12 }}>
            {inspections.map((entry) => (
              <LogEntry key={entry.id} equipmentId={equipmentId} entry={entry} />
            ))}
          </div>
        ) : (
          <EmptyState>
            Der står intet i loggen endnu. Tryk <strong>Skriv service</strong>, når du har lavet
            noget ved værktøjet - hvad du lavede, hvad du skiftede, og gerne et billede.
          </EmptyState>
        )}
      </Card>

      <Card title="Hvem har haft den">
        {loans && loans.length > 0 ? (
          <div className="stack" style={{ gap: 6 }}>
            {loans.slice(0, 10).map((l) => (
              <div key={l.id} className="row" style={{ gap: 8, alignItems: 'center' }}>
                <Avatar user={l.user} size={22} />
                <div className="tiny">
                  <strong>{l.user.displayName}</strong> · {formatDay(l.handedOutOn)}
                  {l.returnedOn ? ` - ${formatDay(l.returnedOn)}` : ' - har den stadig'}
                  {l.projectName && ` · ${l.projectName}`}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState>Værktøjet har ikke været ude endnu.</EmptyState>
        )}
      </Card>

      {reserving && (
        <ReserveDialog equipment={item} onClose={() => setReserving(false)} />
      )}

      {writingService && (
        <ServiceDialog equipment={item} onClose={() => setWritingService(false)} />
      )}
    </div>
  )
}

/**
 * Én post i loggen: hvad der blev lavet, hvad der blev skiftet, og billederne.
 *
 * Billederne ligger bag login og hentes ét ad gangen - en log med tyve fotos må
 * ikke tvinge hele bunken ned over mobilnettet, før siden kan vises.
 */
function LogEntry({ equipmentId, entry }: { equipmentId: number; entry: EquipmentInspection }) {
  const addPhoto = useAddEquipmentLogPhoto()
  const { user, isProjectManager } = useAuth()
  const fileInput = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  const isInspection = entry.kind === 'inspection'
  const mine = entry.registeredBy?.id === user?.id

  async function pick(file: File | undefined) {
    if (!file) return
    setError(null)

    try {
      const prepared = await prepareDocumentFile(file)
      await addPhoto.mutateAsync({
        id: equipmentId,
        entryId: entry.id,
        file: prepared.blob,
        fileName: prepared.fileName
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Billedet kunne ikke gemmes.')
    }
  }

  return (
    <div style={{ borderLeft: `3px solid ${isInspection ? '#2E2A85' : '#94A3B8'}`, paddingLeft: 10 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong className="small">{isInspection ? 'Eftersyn' : 'Service'}</strong>
        <span className="tiny muted">{formatDay(entry.performedOn)}</span>
        {entry.registeredBy && <span className="tiny muted">· {entry.registeredBy.displayName}</span>}
        {isInspection && (
          <span className="tiny muted">· {entry.isApproved ? 'i orden' : 'med bemærkninger'}</span>
        )}
        {entry.performedBy && <span className="tiny muted">· udført af {entry.performedBy}</span>}
      </div>

      {entry.workPerformed && <div className="small" style={{ marginTop: 4 }}>{entry.workPerformed}</div>}

      {entry.partsReplaced && (
        <div className="small" style={{ marginTop: 2 }}>
          <span className="muted">Skiftet: </span>
          {entry.partsReplaced}
        </div>
      )}

      {entry.notes && <div className="tiny muted" style={{ marginTop: 2 }}>{entry.notes}</div>}

      {entry.photos.length > 0 && (
        <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {entry.photos.map((photo) => (
            <LogPhoto key={photo.id} photo={photo} />
          ))}
        </div>
      )}

      {(mine || isProjectManager) && (
        <div style={{ marginTop: 6 }}>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={(e) => void pick(e.target.files?.[0])}
          />
          <button
            className="btn btn--sm"
            disabled={addPhoto.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {addPhoto.isPending ? 'Gemmer ...' : '📷 Tilføj billede'}
          </button>
        </div>
      )}

      {error && <ErrorBox error={error} />}
    </div>
  )
}

/** Ét billede i loggen. Åbner i fuld størrelse ved tryk. */
function LogPhoto({ photo }: { photo: EquipmentLogPhoto }) {
  const url = useAuthedBlob(photo.url)

  if (!url) {
    return <div style={{ width: 72, height: 72, borderRadius: 6, background: '#F1F5F9' }} />
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" title={photo.caption ?? 'Billede fra loggen'}>
      <img
        src={url}
        alt={photo.caption ?? 'Billede fra loggen'}
        style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6 }}
      />
    </a>
  )
}

/**
 * "Skriv service": hvad blev der lavet, hvad blev skiftet, og billeder.
 *
 * <b>Alle montører må skrive her</b> (beslutning 096) - den, der skifter
 * klingen, er den eneste, der ved, at den blev skiftet. Eftersynet, der rykker
 * næste eftersynsdato, laves stadig af en administrator under Værktøj.
 */
function ServiceDialog({ equipment, onClose }: { equipment: Equipment; onClose: () => void }) {
  const add = useAddEquipmentInspection()
  const addPhoto = useAddEquipmentLogPhoto()

  const [performedOn, setPerformedOn] = useState(today())
  const [workPerformed, setWorkPerformed] = useState('')
  const [partsReplaced, setPartsReplaced] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  const busy = add.isPending || addPhoto.isPending

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    try {
      const entry = await add.mutateAsync({
        id: equipment.id,
        kind: 'service',
        performedOn,
        workPerformed: workPerformed.trim() || null,
        partsReplaced: partsReplaced.trim() || null
      })

      // Posten foerst, billederne bagefter - de skal haenge paa noget.
      for (const file of files) {
        const prepared = await prepareDocumentFile(file)
        await addPhoto.mutateAsync({
          id: equipment.id,
          entryId: entry.id,
          file: prepared.blob,
          fileName: prepared.fileName
        })
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Servicen kunne ikke gemmes.')
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Service på {equipment.name}</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Skriv hvad du har lavet, og hvad du har skiftet. Det står i værktøjets log bagefter.
        </p>

        <form onSubmit={(e) => void submit(e)}>
          <label className="field">
            <span className="field__label">Hvornår</span>
            <input
              type="date"
              value={performedOn}
              max={today()}
              onChange={(e) => setPerformedOn(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Hvad har du lavet</span>
            <textarea
              rows={3}
              value={workPerformed}
              placeholder="F.eks. renset, smurt og efterspændt"
              onChange={(e) => setWorkPerformed(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Hvad har du skiftet</span>
            <input
              value={partsReplaced}
              placeholder="F.eks. kul og netledning"
              onChange={(e) => setPartsReplaced(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Billeder (valgfri)</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            />
            {files.length > 0 && (
              <span className="tiny muted">
                {files.length} {files.length === 1 ? 'billede' : 'billeder'} klar
              </span>
            )}
          </label>

          {error && <ErrorBox error={error} />}

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn--primary" type="submit" disabled={busy}>
              {busy ? 'Gemmer ...' : 'Gem service'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Annullér
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/**
 * Adressen, QR-mærkatet på værktøjet peger på: <c>/m/:code</c> (beslutning 096).
 *
 * Siden slår koden op og sender videre til værktøjets egen side. Den findes som
 * sin egen adresse, fordi mobilens eget kamera åbner den URL, der står i koden -
 * og et id ville ikke kunne stå på et mærkat, der blev printet, før tingen fik det.
 */
export function EquipmentCodeLandingPage() {
  const { code } = useParams()
  const { data, isLoading, error } = useEquipmentByCode(code)

  if (isLoading) return <Loading />

  if (error || !data) {
    return (
      <Card title="Værktøjet blev ikke fundet">
        <div className="small muted">
          {error instanceof Error ? error.message : 'Der er ikke noget værktøj med den kode.'}
        </div>
        <p className="small muted">
          Koden på mærkatet var <code>{code}</code>.
        </p>
        <Link className="btn btn--sm" to="/equipment">
          Til værktøjet
        </Link>
      </Card>
    )
  }

  return <Navigate to={`/equipment/${data.id}`} replace />
}

/** Reservationen: fra hvilken dag, og hvor mange dage man regner med. */
function ReserveDialog({ equipment, onClose }: { equipment: Equipment; onClose: () => void }) {
  const reserve = useReserveEquipment()
  const { data: projects } = useProjects()

  const [fromDate, setFromDate] = useState(today())
  const [days, setDays] = useState('1')
  const [projectId, setProjectId] = useState(0)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    try {
      await reserve.mutateAsync({
        id: equipment.id,
        fromDate,
        days: Number(days) || 1,
        projectId: projectId || null,
        note: note.trim() || null
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reservationen kunne ikke gemmes.')
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Reservér {equipment.name}</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Andre kan se, at du skal bruge den - og de kan stille sig bagefter i køen.
        </p>

        <form onSubmit={(e) => void submit(e)}>
          <div className="row" style={{ gap: 10 }}>
            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Fra hvilken dag</span>
              <input type="date" value={fromDate} min={today()} onChange={(e) => setFromDate(e.target.value)} />
            </label>

            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Hvor mange dage</span>
              <input
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
            </label>
          </div>

          <label className="field">
            <span className="field__label">Til hvilken sag (valgfri)</span>
            <select value={projectId} onChange={(e) => setProjectId(Number(e.target.value))}>
              <option value={0}>Ingen bestemt sag</option>
              {(projects?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {projectOptionLabel(p)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Hvad skal den bruges til (valgfri)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} />
          </label>

          {error && <ErrorBox error={error} />}

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn--primary" type="submit" disabled={reserve.isPending}>
              {reserve.isPending ? 'Gemmer ...' : 'Reservér'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Annullér
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
